import { _ } from "../../../../../core/polyglot/polyglot.js";
import { formatFullNoDecimals } from "../../../../../core/helpers/currency-formatting-util.js";
import { insertBetween } from "@foretagsplatsen/core-helpers/array";
import AbstractRuleModelVisitor from "../../../../../models/forecast/ruleModels/AbstractRuleModelVisitor.js";
import percentage from "../../../../../core/percentage.js";

/**
 * Visitor of the {@link AbstractRuleModel} hierarchy returning a
 * renderable presenting in a sentence the fields of a rule model.
 *
 * @param {Budget[]} budgets - This is used to render a
 *   {@link FromBudgetPredictionRuleModel}
 */
export default class RuleModelFieldVisitor extends AbstractRuleModelVisitor {
	constructor({ budgets }) {
		super(...arguments);

		this._budgets = budgets;
	}

	visitConstantPredictionRuleModel(ruleModel) {
		const initialValue = (html) => {
			html.b(formatFullNoDecimals(ruleModel.getInitialValue()));
		};

		return this._growthPercentageRenderer({
			ruleModel,
			getSentence: ({ absoluteGrowthValue, growthOrDeclineWord }) =>
				_(
					"Initial value of {initialValue}, with {absoluteGrowthValue} monthly {growthOrDeclineWord}.",
					{ initialValue, absoluteGrowthValue, growthOrDeclineWord },
				),
		});
	}

	visitFromActualsPredictionRuleModel(ruleModel) {
		const numberOfMonths = (html) => html.b(ruleModel.getShift());

		return this._growthPercentageRenderer({
			ruleModel,
			getSentence: ({ absoluteGrowthValue, growthOrDeclineWord }) =>
				_(
					"Looking back {numberOfMonths} months, with {absoluteGrowthValue} monthly {growthOrDeclineWord}.",
					{
						numberOfMonths,
						absoluteGrowthValue,
						growthOrDeclineWord,
					},
				),
		});
	}

	visitSeasonalTrendPredictionRuleModel(ruleModel) {
		return this._ruleModelShiftRenderer(ruleModel);
	}

	visitFromBudgetPredictionRuleModel(ruleModel) {
		const budget = this._budgets.find(
			(each) => each.getId() === ruleModel.getBudgetId(),
		);

		return _("Using {budget} budget.", {
			budget: (html) => html.b(budget.getName()),
		});
	}

	visitLinearRegressionPredictionRuleModel(ruleModel) {
		return this._ruleModelShiftRenderer(ruleModel);
	}

	visitSingularSpectrumAnalysisRuleModel(ruleModel) {
		return this._ruleModelShiftRenderer(ruleModel);
	}

	visitBalanceRuleModel(ruleModel) {
		const spread = ruleModel.getPaymentSpread().map((amount, index) =>
			_("{value} month {monthIndex}", {
				value: (html) => html.b(percentage(amount / 100)),
				monthIndex: index + 1,
			}),
		);

		return _(
			"{vatRate} rate ({accountType}) - Payment spread {paymentSpread}.",
			{
				vatRate: (html) =>
					html.b(percentage(ruleModel.getVatRateValue() / 100)),
				accountType: (html) => html.b(ruleModel.getAccountTypeName()),
				paymentSpread: insertBetween({
					array: spread,
					separator: ", ",
				}),
			},
		);
	}

	_growthPercentageRenderer({ ruleModel, getSentence }) {
		const growthPercentage = ruleModel.getGrowthPercentage();

		const absoluteGrowthValue = (html) =>
			html.b(percentage(Math.abs(growthPercentage) / 100));

		const growthOrDeclineWord = (html) =>
			html.b(growthPercentage < 0 ? _("decline") : _("growth"));

		return getSentence({ absoluteGrowthValue, growthOrDeclineWord });
	}

	_ruleModelShiftRenderer(ruleModel) {
		const numberOfMonths = (html) => html.b(ruleModel.getShift());

		return _("Looking back {numberOfMonths} months.", { numberOfMonths });
	}
}
